import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { tiposHoraApi } from '../../features/cadastros-gerais/tipos-hora/api';
import type { TipoHoraListItem } from '../../features/cadastros-gerais/tipos-hora/types';

export interface TipoHoraSelectProps {
  value: number | null;
  onChange: (id: number | null, item: TipoHoraListItem | null) => void;
  label?: string;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

export function TipoHoraSelect({
  value,
  onChange,
  label,
  error,
  helperText,
  disabled,
}: TipoHoraSelectProps): JSX.Element {
  const { t } = useTranslation();

  // Cadastro pequeno (< 50 itens em produção) — carrega lista completa uma vez.
  const list = useQuery({
    queryKey: ['hour-types', 'select-all'],
    queryFn: () =>
      tiposHoraApi.list({
        page: 1,
        pageSize: 200,
        sortBy: 'sigla',
        sortOrder: 'asc',
      }),
    staleTime: 5 * 60 * 1000,
  });

  const options: TipoHoraListItem[] = (list.data?.data ?? []).filter((o) => o.ativo);
  const selected: TipoHoraListItem | null = options.find((o) => o.id === value) ?? null;

  return (
    <Autocomplete<TipoHoraListItem>
      value={selected}
      onChange={(_, v) => onChange(v?.id ?? null, v)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) => `${o.sigla} — ${o.nome}`}
      options={options}
      loading={list.isFetching}
      disabled={disabled}
      noOptionsText={t('components.asyncSelect.noOptions')}
      loadingText={t('components.asyncSelect.loading')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t('components.tipoHoraSelect.label')}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {list.isFetching ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
